import React, { useState, useEffect } from 'react';
import { Midi } from '@tonejs/midi';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const noteNames = ['C', 'C♯', 'D', 'D♯', 'E', 'F', 'F♯', 'G', 'G♯', 'A', 'A♯', 'B'];
const ecurve = [0, 0.2574, 0.4036, 0.5640, 0.7256, 0.7737, 0.7797, 0.4915, 0.9183, 0.8747, 0.7049, 0.3185];

function App() {
  const [rawNotes, setRawNotes] = useState([]);
  const [ppq, setPpq] = useState(480);
  const [energy, setEnergy] = useState([]);
  const [notes, setNotes] = useState([]);
  const [normalize, setNormalize] = useState(true);
  const [groupSize, setGroupSize] = useState(1);
  const [showDescription, setShowDescription] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [tonicClass, setTonicClass] = useState(0); // C

  const handleFile = async (e) => {
    setErrorMsg('');
    const file = e.target.files[0];

    try {
      const arrayBuffer = await file.arrayBuffer();
      const midi = new Midi(arrayBuffer);

      const combinedNotes = midi.tracks.flatMap(track => track.notes);
      setRawNotes(combinedNotes);
      setPpq(midi.header.ppq || 480);
    } catch (err) {
      console.error(err);
      setErrorMsg('There was a problem parsing the MIDI file.');
      setRawNotes([]);
      setNotes([]);
      setEnergy([]);
    }
  };

  useEffect(() => {
    if (rawNotes.length === 0) return;

    const localNotes = [];
    const localEnergy = [];
    const d = 12;

    rawNotes.forEach(note => {
      const pitch = note.midi;
      const duration = note.duration;
      const ticks = note.ticks;
      const time = note.time;

      const n = pitch % d;
      const mod12 = ((n - tonicClass + d) % d);

      const beat = ticks / ppq;
      const measure = Math.floor(beat / 4);
      const beatInMeasure = beat % 4;

      let beatFactor = 1;
      const tol = 0.1;
      if (Math.abs(beatInMeasure - 0) < tol) {
        beatFactor = 2;
      } else if (Math.abs(beatInMeasure - 2) < tol) {
        beatFactor = 1.5;
      }

      const e = beatFactor * ecurve[mod12] + Math.random() / 20;
      localEnergy.push(e);

      localNotes.push({
        time,
        duration,
        pitch,
        ticks,
        beat,
        measure: measure + 1
      });
    });

    setEnergy(localEnergy);
    setNotes(localNotes);
  }, [tonicClass, rawNotes, ppq]);

  const normalizeArray = (arr) => {
    const min = Math.min(...arr);
    const max = Math.max(...arr);
    if (max === min) return arr.map(() => 0.5);
    return arr.map(x => (x - min) / (max - min));
  };

  const getGroupedEnergy = (N = 1) => {
    const map = new Map();
    notes.forEach((note, i) => {
      const m = note.measure;
      if (!map.has(m)) map.set(m, []);
      map.get(m).push({ e: energy[i], dur: note.duration });
    });

    const groupedLabels = [];
    const groupedData = [];
    const measures = [...map.keys()].sort((a, b) => a - b);
    const numChunks = Math.floor(measures.length / N);

    for (let i = 0; i < numChunks; i++) {
      const group = measures.slice(i * N, (i + 1) * N);
      let weightedSum = 0;
      let totalDur = 0;
      group.forEach(m => {
        const entries = map.get(m);
        entries.forEach(({ e, dur }) => {
          weightedSum += e * dur;
          totalDur += dur;
        });
      });
      const avg = totalDur > 0 ? weightedSum / totalDur : 0;
      groupedLabels.push(`M${group[0]}–${group[group.length - 1]}`);
      groupedData.push(avg);
    }

    return { labels: groupedLabels, data: groupedData };
  };

  const groupedEnergy = getGroupedEnergy(groupSize);
  const displayData = normalize ? normalizeArray(groupedEnergy.data) : groupedEnergy.data;
  const xMin = 1;
  const xMax = groupedEnergy.labels.length;

  const chartData = {
    labels: groupedEnergy.labels.map((_, i) => i + 1),
    datasets: [{
      label: `${groupSize}M Avg Energy`,
      data: displayData,
      borderColor: 'rgba(75,192,192,1)',
      backgroundColor: 'rgba(75,192,192,0.2)',
      borderWidth: 2,
      pointRadius: 3,
      tension: 0.3
    }]
  };

  const tcb = groupSize === 1 ? val => `M${val}` : val => `G${val}`;
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: 'linear',
        min: xMin,
        max: xMax,
        ticks: {
          stepSize: 1,
          callback: tcb,
        },
        title: {
          display: true,
          text: `${groupSize}-Measure Groups`
        },
        grid: {
          drawOnChartArea: true
        }
      },
      y: {
        min: 0,
        max: 1,
        title: {
          display: true,
          text: 'Tonal Energy'
        }
      }
    }
  };

  return (
    <div style={{ padding: 20 }}>
      <h2>Tonal Energy Curve</h2>

      <div style={{ marginBottom: 10 }}>
        <button onClick={() => setShowDescription(!showDescription)}>
          {showDescription ? 'Hide' : 'Show'} Description
        </button>
        <button onClick={() => setGroupSize(groupSize === 1 ? 2 : 1)} style={{ marginLeft: 10 }}>
          Group by {groupSize === 1 ? '2M' : '1M'}
        </button>
      </div>

      {showDescription && (
        <p style={{ maxWidth: 600, fontSize: '0.9rem', marginBottom: 20 }}>
          This curve is generated by an overtone-based model of tonal energy. It reflects perceptual tension and resolution in melody over time, grouped by 1 or 2 measures.<br /><br />
          Upload a single-track .midi file to see your music's energy curve. A time signature of 4/4 is assumed.
        </p>
      )}

      <div style={{ marginBottom: 10 }}>
        <label>
          Tonic:&nbsp;
          <select value={tonicClass} onChange={(e) => setTonicClass(parseInt(e.target.value))}>
            {noteNames.map((name, i) => (
              <option key={i} value={i}>{name}</option>
            ))}
          </select>
        </label>
      </div>

      <input type="file" accept=".mid" onChange={handleFile} />

      {errorMsg && (
        <div style={{ color: 'red', marginTop: 10 }}>{errorMsg}</div>
      )}

      {energy.length > 0 && notes.length > 0 && (
        <div style={{ height: '350px', width: '100%', marginTop: 20 }}>
          <Line data={chartData} options={chartOptions} />
        </div>
      )}
    </div>
  );
}

export default App;
